import {
  COMMON_CURRENCIES,
  RECEIPT_IMAGE_RETENTION_CHOICES,
  type ShoppingPreferences,
} from '@recipes/contracts';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiConsent, useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { storeShoppingPreferences } from './device-copy';
import { listTypeOptions } from './list-types';
import { useShoppingSyncState } from './hooks';
import { currentFormatLocale } from '@/lib/i18n';

const DEFAULT_CURRENCY = '__default__';
const OTHER_CURRENCY = '__other__';

/**
 * Shopping-list settings on the profile page (docs/partial/shopping-list-extensions-spec.md 5.5, 5.6,
 * 6.6). Saved immediately. Everything that acts on the user's behalf is off by default.
 */
export function ShoppingPreferencesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const aiEnabled = useFeature('ai');
  const speechEnabled = useFeature('speech');
  const consented = useAiConsent();
  const syncState = useShoppingSyncState();
  const catalogEnabled = useFeature('ingredient_catalog');
  const pricesEnabled = useFeature('prices') && catalogEnabled;
  const receiptsEnabled = useFeature('receipt_scan') && pricesEnabled && aiEnabled;
  const { data: config } = useConfig();
  const [otherCurrency, setOtherCurrency] = useState<string | null>(null);
  const [typesDraft, setTypesDraft] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Partial<ShoppingPreferences>) =>
      api.users.updateProfile({ shoppingPreferences: patch }),
    onSuccess: async (next) => {
      queryClient.setQueryData(queryKeys.profile, next);
      // The list pages read the settings from the device copy, also without a connection.
      await storeShoppingPreferences(next.shoppingPreferences);
    },
    onError: (e) => toastError(e),
  });
  const saveCurrency = useMutation({
    mutationFn: (currency: string | null) => api.users.updateProfile({ currency }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.profile, next);
      setOtherCurrency(null);
    },
    onError: (e) => toastError(e),
  });

  if (!profile) return null;
  const prefs = profile.shoppingPreferences;
  const syncedAt = syncState?.syncedAt
    ? new Intl.DateTimeFormat(currentFormatLocale(), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(syncState.syncedAt))
    : null;

  return (
    <section
      className="mt-8 space-y-4 border-t pt-6"
      aria-labelledby="shopping-prefs-heading"
      data-testid="shopping-preferences"
    >
      <h2 id="shopping-prefs-heading" className="text-lg font-semibold">
        {t('shopping.prefs.title')}
      </h2>

      <div className="space-y-1">
        <Label htmlFor="shopping-list-types">{t('shopping.prefs.listTypes')}</Label>
        <p className="text-xs text-muted-foreground">{t('shopping.prefs.listTypesHint')}</p>
        <Input
          id="shopping-list-types"
          value={typesDraft ?? listTypeOptions(prefs.listTypes, t).join(', ')}
          maxLength={400}
          onChange={(e) => setTypesDraft(e.target.value)}
          onBlur={() => {
            if (typesDraft === null) return;
            const listTypes = [
              ...new Set(
                typesDraft
                  .split(',')
                  .map((x) => x.trim().slice(0, 40))
                  .filter(Boolean),
              ),
            ].slice(0, 20);
            setTypesDraft(null);
            if (listTypes.join('|') !== listTypeOptions(prefs.listTypes, t).join('|'))
              save.mutate({ listTypes });
          }}
          data-testid="shopping-list-types"
        />
      </div>
      <div>
        <Label htmlFor="shopping-reactivation">{t('shopping.prefs.reactivation')}</Label>
        <p className="text-xs text-muted-foreground">{t('shopping.prefs.reactivationHint')}</p>
        <Select
          value={prefs.reactivation}
          onValueChange={(v) =>
            save.mutate({ reactivation: v as ShoppingPreferences['reactivation'] })
          }
          disabled={save.isPending}
        >
          <SelectTrigger
            id="shopping-reactivation"
            className="mt-1 max-w-sm"
            data-testid="shopping-prefs-reactivation"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="REPLACE">{t('shopping.prefs.reactivationReplace')}</SelectItem>
            <SelectItem value="ASK">{t('shopping.prefs.reactivationAsk')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {speechEnabled && (
        <div className="flex items-start gap-3">
          <Switch
            id="shopping-hands-free"
            checked={prefs.handsFree}
            disabled={save.isPending}
            onCheckedChange={(handsFree) => save.mutate({ handsFree })}
            data-testid="shopping-prefs-hands-free"
          />
          <div>
            <Label htmlFor="shopping-hands-free">{t('shopping.prefs.handsFree')}</Label>
            <p className="text-xs text-muted-foreground">{t('shopping.prefs.handsFreeHint')}</p>
          </div>
        </div>
      )}

      {speechEnabled && (
        <div className="flex items-start gap-3">
          <Switch
            id="shopping-voice-auto-stop"
            checked={prefs.voiceAutoStop}
            disabled={save.isPending}
            onCheckedChange={(voiceAutoStop) => save.mutate({ voiceAutoStop })}
            data-testid="shopping-prefs-voice-auto-stop"
          />
          <div>
            <Label htmlFor="shopping-voice-auto-stop">{t('shopping.prefs.voiceAutoStop')}</Label>
            <p className="text-xs text-muted-foreground">{t('shopping.prefs.voiceAutoStopHint')}</p>
            {prefs.voiceAutoStop && (
              <div className="mt-2">
                <Label htmlFor="shopping-voice-silence">{t('shopping.prefs.voiceSilence')}</Label>
                <Select
                  value={String(prefs.voiceSilenceSeconds)}
                  onValueChange={(v) =>
                    save.mutate({
                      voiceSilenceSeconds: Number(v) as ShoppingPreferences['voiceSilenceSeconds'],
                    })
                  }
                  disabled={save.isPending}
                >
                  <SelectTrigger
                    id="shopping-voice-silence"
                    className="mt-1 max-w-[12rem]"
                    data-testid="shopping-prefs-voice-silence"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2, 3, 5].map((seconds) => (
                      <SelectItem key={seconds} value={String(seconds)}>
                        {t('shopping.prefs.voiceSilenceSeconds', { seconds })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {speechEnabled && aiEnabled && (
        <div className="flex items-start gap-3">
          <Switch
            id="shopping-ai-voice"
            checked={prefs.aiVoiceParsing}
            disabled={save.isPending || (!consented && !prefs.aiVoiceParsing)}
            onCheckedChange={(aiVoiceParsing) => save.mutate({ aiVoiceParsing })}
            data-testid="shopping-prefs-ai-voice"
          />
          <div>
            <Label htmlFor="shopping-ai-voice">{t('shopping.prefs.aiVoice')}</Label>
            <p className="text-xs text-muted-foreground">
              {consented
                ? t('shopping.prefs.aiVoiceHint')
                : t('shopping.prefs.aiVoiceNeedsConsent')}
            </p>
          </div>
        </div>
      )}

      {catalogEnabled && (
        <div>
          <Label htmlFor="shopping-scan-check-off">{t('shopping.prefs.scanCheckOff')}</Label>
          <p className="text-xs text-muted-foreground">{t('shopping.prefs.scanCheckOffHint')}</p>
          <Select
            value={prefs.scanCheckOff}
            onValueChange={(v) =>
              save.mutate({ scanCheckOff: v as ShoppingPreferences['scanCheckOff'] })
            }
            disabled={save.isPending}
          >
            <SelectTrigger
              id="shopping-scan-check-off"
              className="mt-1 max-w-sm"
              data-testid="shopping-prefs-scan-check-off"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ASK">{t('shopping.prefs.scanCheckOffAsk')}</SelectItem>
              <SelectItem value="AUTO">{t('shopping.prefs.scanCheckOffAuto')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {pricesEnabled && (
        <div className="flex items-start gap-3">
          <Switch
            id="shopping-show-prices"
            checked={prefs.showPrices}
            disabled={save.isPending}
            onCheckedChange={(showPrices) => save.mutate({ showPrices })}
            data-testid="shopping-prefs-show-prices"
          />
          <div>
            <Label htmlFor="shopping-show-prices">{t('shopping.prefs.showPrices')}</Label>
            <p className="text-xs text-muted-foreground">{t('shopping.prefs.showPricesHint')}</p>
          </div>
        </div>
      )}

      {pricesEnabled && (
        <div>
          <Label htmlFor="shopping-currency">{t('shopping.prefs.currency')}</Label>
          <p className="text-xs text-muted-foreground">{t('shopping.prefs.currencyHint')}</p>
          <Select
            value={
              otherCurrency !== null
                ? OTHER_CURRENCY
                : profile.currency === null
                  ? DEFAULT_CURRENCY
                  : (COMMON_CURRENCIES as readonly string[]).includes(profile.currency)
                    ? profile.currency
                    : OTHER_CURRENCY
            }
            onValueChange={(v) => {
              if (v === OTHER_CURRENCY) setOtherCurrency(profile.currency ?? '');
              else saveCurrency.mutate(v === DEFAULT_CURRENCY ? null : v);
            }}
            disabled={saveCurrency.isPending}
          >
            <SelectTrigger
              id="shopping-currency"
              className="mt-1 max-w-sm"
              data-testid="shopping-prefs-currency"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_CURRENCY}>
                {t('shopping.prefs.currencyDefault', {
                  currency: config?.prices?.defaultCurrency ?? profile.effectiveCurrency,
                })}
              </SelectItem>
              {COMMON_CURRENCIES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
              <SelectItem value={OTHER_CURRENCY}>{t('shopping.prefs.currencyOther')}</SelectItem>
            </SelectContent>
          </Select>
          {(otherCurrency !== null ||
            (profile.currency !== null &&
              !(COMMON_CURRENCIES as readonly string[]).includes(profile.currency))) && (
            <form
              className="mt-2 flex max-w-sm items-start gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const code = (otherCurrency ?? '').trim().toUpperCase();
                if (/^[A-Z]{3}$/.test(code)) saveCurrency.mutate(code);
              }}
            >
              <div className="flex-1">
                <Input
                  value={otherCurrency ?? profile.currency ?? ''}
                  onChange={(event) => setOtherCurrency(event.target.value)}
                  maxLength={3}
                  autoCapitalize="characters"
                  aria-label={t('shopping.prefs.currencyOther')}
                  aria-invalid={
                    otherCurrency !== null && !/^[A-Za-z]{3}$/.test(otherCurrency.trim())
                  }
                  data-testid="shopping-prefs-currency-other"
                />
                {otherCurrency !== null && !/^[A-Za-z]{3}$/.test(otherCurrency.trim()) && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('shopping.prefs.currencyInvalid')}
                  </p>
                )}
              </div>
              <Button type="submit" variant="outline" loading={saveCurrency.isPending}>
                {t('common.save')}
              </Button>
            </form>
          )}
        </div>
      )}

      {receiptsEnabled && (
        <div>
          <Label htmlFor="shopping-receipt-retention">{t('shopping.prefs.receiptRetention')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('shopping.prefs.receiptRetentionHint')}
          </p>
          <Select
            value={String(prefs.receiptImageRetentionDays)}
            onValueChange={(v) =>
              save.mutate({
                receiptImageRetentionDays: Number(
                  v,
                ) as ShoppingPreferences['receiptImageRetentionDays'],
              })
            }
            disabled={save.isPending}
          >
            <SelectTrigger
              id="shopping-receipt-retention"
              className="mt-1 max-w-sm"
              data-testid="shopping-prefs-receipt-retention"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECEIPT_IMAGE_RETENTION_CHOICES.filter(
                (days) => days <= (config?.prices?.receiptImageRetentionMaxDays ?? 365),
              ).map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {days === 0
                    ? t('shopping.prefs.receiptRetention0')
                    : t('shopping.prefs.receiptRetentionDays', { days })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <p className="text-xs text-muted-foreground" data-testid="shopping-prefs-synced">
        {syncedAt
          ? t('shopping.prefs.syncedAt', { time: syncedAt })
          : t('shopping.prefs.notSynced')}
      </p>
    </section>
  );
}
